import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import Logo from '@/components/Logo';
import ProductCard from '@/components/ProductCard';
import {
  useCatalogue,
  useCatalogueLines,
  useDebounced,
  type Filters,
  type Sort,
} from '@/lib/catalogue';
import type { MeResponse } from '@/lib/api';
import { cn } from '@/lib/utils';

const TRIS: { value: Sort; label: string }[] = [
  { value: 'pertinence', label: 'Vedettes d\'abord' },
  { value: 'part', label: 'N° de pièce' },
  { value: 'description', label: 'Description' },
  { value: 'prix', label: 'Prix croissant' },
];

export default function Catalogue({ session }: { session: MeResponse }) {
  const [q, setQ] = useState('');
  const [lineCode, setLineCode] = useState<string | null>(null);
  const [featuredOnly, setFeaturedOnly] = useState(false);
  const [sort, setSort] = useState<Sort>('pertinence');

  const debouncedQ = useDebounced(q);
  const filters: Filters = useMemo(
    () => ({ q: debouncedQ, lineCode, featuredOnly, sort }),
    [debouncedQ, lineCode, featuredOnly, sort],
  );

  const { products, total, isPending, isError, error, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useCatalogue(filters);
  const lines = useCatalogueLines();

  const mesLignes = lines.data?.lines ?? [];
  const plusieursLignes = mesLignes.length > 1;
  const filtreActif = q !== '' || lineCode !== null || featuredOnly;

  return (
    <div className="flex min-h-full flex-col">
      <header className="bg-card sticky top-0 z-10 space-y-3 border-b p-4">
        <div className="flex items-center gap-3">
          <Link to="/" className="border-input flex h-14 items-center rounded-lg border px-4 font-medium">
            ← Accueil
          </Link>
          <Logo className="h-9" />
          <h1 className="text-xl font-bold">Catalogue</h1>
          <span className="text-muted-foreground ml-auto text-sm">
            {session.user.fullName}
          </span>
        </div>

        {/* Champ de recherche : 64 px de haut, tapable sans viser. */}
        <div className="relative">
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="N° de pièce ou description…"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            className="border-input bg-background focus:ring-ring h-16 w-full rounded-lg border px-4 pr-28 text-lg focus:ring-2 focus:outline-none"
          />
          {q !== '' && (
            <button
              onClick={() => setQ('')}
              className="text-muted-foreground absolute top-1/2 right-3 h-12 -translate-y-1/2 rounded-lg px-4 text-sm font-medium"
            >
              Effacer
            </button>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {/* Filtre par ligne : affiché seulement si le rep en a plusieurs. */}
          {plusieursLignes && (
            <>
              <FilterChip active={lineCode === null} onClick={() => setLineCode(null)}>
                Toutes
              </FilterChip>
              {mesLignes.map((l) => (
                <FilterChip
                  key={l.id}
                  active={lineCode === l.code}
                  onClick={() => setLineCode(lineCode === l.code ? null : l.code)}
                >
                  {l.code}
                  <span className="text-muted-foreground ml-1.5 text-xs">{l.productCount}</span>
                </FilterChip>
              ))}
              <span className="bg-border mx-1 h-8 w-px" />
            </>
          )}

          <FilterChip active={featuredOnly} onClick={() => setFeaturedOnly(!featuredOnly)}>
            Vedettes
          </FilterChip>

          <select
            value={sort}
            onChange={(e) => setSort(e.target.value as Sort)}
            className="border-input bg-background ml-auto h-12 rounded-lg border px-3 text-sm"
          >
            {TRIS.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </div>
      </header>

      <div className="flex-1 space-y-3 p-4">
        <p className="text-muted-foreground text-sm" aria-live="polite">
          {isPending
            ? 'Chargement…'
            : `${total} produit${total > 1 ? 's' : ''}${filtreActif ? ' correspondent au filtre' : ''}`}
        </p>

        {isError && (
          <div className="border-destructive/40 bg-destructive/10 rounded-lg border p-4">
            <p className="text-destructive font-medium">Catalogue indisponible.</p>
            <p className="text-muted-foreground mt-1 text-sm">{error?.message}</p>
          </div>
        )}

        {!isPending && !isError && products.length === 0 && (
          <div className="rounded-xl border border-dashed p-10 text-center">
            <p className="font-medium">Aucun produit ne correspond.</p>
            <p className="text-muted-foreground mt-1 text-sm">
              {q !== '' ? `Aucun résultat pour « ${q} ».` : 'Ajustez les filtres.'}
            </p>
            {filtreActif && (
              <button
                onClick={() => {
                  setQ('');
                  setLineCode(null);
                  setFeaturedOnly(false);
                }}
                className="border-input mt-4 h-12 rounded-lg border px-5 font-medium"
              >
                Réinitialiser les filtres
              </button>
            )}
          </div>
        )}

        {/* lg (1024 px) et non xl (1280 px) : l'iPad Pro 11 en paysage fait
            1194 px de large. Avec xl, la bascule à deux colonnes ne se
            déclenchait jamais sur l'appareil cible. */}
        <div data-testid="grille-produits" className="grid gap-3 lg:grid-cols-2">
          {products.map((p) => (
            <ProductCard key={p.id} product={p} />
          ))}
        </div>

        {hasNextPage && (
          <button
            onClick={() => void fetchNextPage()}
            disabled={isFetchingNextPage}
            className="border-input h-16 w-full rounded-lg border text-lg font-medium disabled:opacity-50"
          >
            {isFetchingNextPage ? 'Chargement…' : `Afficher plus (${products.length} sur ${total})`}
          </button>
        )}
      </div>
    </div>
  );
}

function FilterChip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'h-12 rounded-lg border px-4 font-medium transition-colors',
        active ? 'border-primary bg-primary/10' : 'border-input',
      )}
    >
      {children}
    </button>
  );
}
